import {
  indexSetObjectsForUI,
  isPublicWorkout,
  PublicUserProfile,
  PublicWorkout,
  setObjectToUIFacingSetIndicator,
} from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import API from 'utils/API';
import {
  calculatePublicWorkoutStats,
  calculateSecondColumnHeader,
  calculateSetValue,
} from './utils';
import { ExerciseProps } from './components/types';
import { sendEvent } from 'utils/events';
import { copyToClipboard } from 'utils/clipboard';
import toast from 'react-hot-toast';
import { t } from 'translations/t';
import { stores } from 'state/stores';

export class WorkoutDetailPublicViewModel {
  workout?: PublicWorkout;
  userProfile: PublicUserProfile | undefined;
  isLoading: boolean = false;

  constructor() {
    makeAutoObservable(this);
  }

  refreshContent = async (workoutId: string) => {
    this.isLoading = true;
    try {
      const result = await API.getWorkout(workoutId);

      // This is so ugly, we should have a different endpoint for this!
      if (!isPublicWorkout(result.data)) return;

      const { username } = result.data;
      const userResult = await API.getPublicUserProfile(username);

      this.workout = result.data;
      this.userProfile = userResult.data;
    } finally {
      this.isLoading = false;
    }
  };

  onCopyLinkPress = () => {
    if (!this.workout) return;

    sendEvent('workoutDetail_copyLink', { workoutId: this.workout.id });
    copyToClipboard(window.location.href);
    toast.success(t('global.copiedToClipboard'));
  };

  get stats() {
    if (!this.workout) return [];

    return calculatePublicWorkoutStats(this.workout);
  }

  get exercises(): ExerciseProps[] {
    if (!this.workout) return [];

    const { weight_unit, distance_unit } = this.workout;

    return this.workout.exercises.map(e => {
      const indexedSets = indexSetObjectsForUI(e.sets);

      return {
        key: e.id,
        id: e.exercise_template_id,
        image: e.thumbnail_url ?? undefined,
        title: stores.exerciseTemplates.getExerciseTitle(e.exercise_template_id) || e.title,
        supersetId: e.superset_id ?? undefined,
        notes: e.notes,
        secondColumnHeader: calculateSecondColumnHeader(e),
        sets: e.sets.map((s, setIndex) => ({
          key: `${s.id}`,
          indicator: setObjectToUIFacingSetIndicator(indexedSets[setIndex]),
          reps: s.reps,
          value: calculateSetValue(e, s, {
            weightUnit: weight_unit,
            distanceUnitShort: distance_unit === 'kilometers' ? 'km' : 'mi',
          }),
          rpe: s.rpe,
          prs: s.prs,
        })),
      };
    });
  }
}
